import { CarFleetViewRecord } from '@navio/nsdk';
import { NoddiBasicCard } from 'noddi-ui-web';
import { customerRoutes, formatLicensePlate } from 'noddi-util';
import { CarImage } from 'noddi-web';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCarName } from '../../utils/carName';
import { CarCardMeta } from '../Common/CarCardMeta';

interface CarItemCardProps {
  car: CarFleetViewRecord;
  showBookingSummary?: boolean;
  showDetailsLink?: boolean;
  /**
   * A control under the car's facts, such as Book. The rest of the card still opens the car's details, so no
   * separate details link is needed.
   */
  action?: ReactNode;
}

const CARD_CLASS = 'relative isolate flex overflow-hidden px-4';

/** A car on the home page, laid out like the booking flow's car card: photo bleeding off the right edge. */
export const CarItemCard = ({ car, showBookingSummary = true, showDetailsLink = true, action }: CarItemCardProps) => {
  const navigate = useNavigate();
  const { id, licensePlate, wheelStorage, upcomingBooking, latestCompletedBooking } = car;
  const carName = getCarName({ car });
  const detailsPath = customerRoutes.myCarDetails.getPath({ carId: id });

  const content = (
    <div className='relative flex min-h-17 w-full items-center'>
      <CarImage car={car} alt='' size='bleed' />
      {/* Right padding keeps text off the car, which fills the card's right edge. */}
      <div className='relative flex min-w-0 flex-1 flex-col pr-28'>
        <span className='font-semibold'>{carName}</span>
        <span className='text-sm font-medium tracking-wide tabular-nums'>
          {formatLicensePlate(licensePlate.number)}
        </span>
        <CarCardMeta
          isOnTireHotel={!!wheelStorage.length}
          upcomingBookingDate={showBookingSummary ? upcomingBooking?.date : undefined}
          lastBookingDate={showBookingSummary ? latestCompletedBooking?.date : undefined}
        />
        {action && <div className='mt-2 flex'>{action}</div>}
      </div>
    </div>
  );

  // A control cannot sit inside a link, so a card with an action is a button surface beside it instead.
  return action ? (
    <NoddiBasicCard
      separateAction
      accessibleLabel={carName}
      onClick={() => void navigate(detailsPath)}
      size='small'
      className={CARD_CLASS}
    >
      {content}
    </NoddiBasicCard>
  ) : (
    <NoddiBasicCard to={showDetailsLink ? detailsPath : undefined} size='small' className={CARD_CLASS}>
      {content}
    </NoddiBasicCard>
  );
};
